// Alta + pago "enterprise" desde una URL personalizada (una por plan, ej.
// `/alta-empresa/ultra-1`). Para clientes ya hablados por ventas: NO pasa por la
// generación de imagen de ejemplo del onboarding normal.
//
// Flujo (columna derecha, en pasos): crear cuenta / iniciar sesión → datos de la
// empresa (mismo form que el onboarding) → pago con Rebill → redirige a la app.
// La columna izquierda (resumen del plan) queda fija, igual que `Checkout.tsx`
// (mismo diseño; responsive: 2 columnas en desktop, stackeado en mobile).
// Ver ADR.

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CheckCircle2,
  AlertCircle,
  Loader2,
  Mail,
  Lock,
  ChevronDown,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { fetchStripeCheckoutUrl } from '../lib/paymentServer';
import {
  captureAttributionParams,
  metadataDeAtribucion,
  referrerDeLlegada,
  saveSignupAttribution,
} from '../lib/signupAttribution';
import {
  getPlanById,
  ENTERPRISE_PLAN_SLUGS,
  paisFlags,
  currencyForPais,
  getPlanPriceForPais,
  formatPriceForPais,
  cambioDePlanAdmiteDescuento,
  cobraConRebill,
  destinoPostPago,
} from '../lib/checkoutPlans';
// El paso y el país de esta pantalla se resuelven en UN solo lugar, puro y testeado.
// Nadie de acá llama a `resolverPaisCheckout` por su cuenta: así es como el camino del
// login se quedaba sin país. Ver ADR-2026-09-22-el-pais-del-alta-se-resuelve-en-un-solo-lugar.
import {
  exClienteParaElPrecio,
  paisInicialDelAlta,
  resolverPantallaDelAlta,
  urlDeVueltaDelAlta,
  type EstadoDeCuentaDelAlta,
} from '../lib/altaEmpresaPantalla';
import {
  featuresPlanCustom,
  fetchPlanCustom,
  formatPrecioCustom,
  montoDePrecioCustom,
  nombrePlanSinPrefijo,
  PlanCustomNoDisponible,
  type PlanCustomPublico,
} from '../lib/planesCustom';
import { parametrosDePlan, trackMeta } from '../lib/metaPixel';
import {
  esMailYaRegistrado,
  mensajeDeErrorDeAuth,
} from '../lib/mensajesDeAuth';
import { recordarVueltaDelReset } from '../lib/vueltaAlAlta';
import {
  esPrimeraVezEnEstaCarga,
  esTraficoAutomatico,
  marcarMomento,
  msDesde,
} from '../lib/embudoAltaEmpresa';
import { trackEvent as trackEventEnPostHog } from '../lib/posthog';
import { primerCampoInvalido, type CampoDelRegistro } from '../lib/registroDelAlta';
import { useDebajoDeMd } from '../hooks/useEsMobile';
import { useBfcacheRestore } from '../hooks/useBfcacheRestore';
import { AvisoGoogleEnApp, useAvisoGoogleEnApp } from '../components/AvisoGoogleEnApp';
import { leerUltimoLogin } from '../lib/ultimoLogin';
import { Icon } from '../design-system/icons';
import { useUserGroup } from '../hooks/useUserGroup';
import { CountrySelect } from '../components/OnboardingSelects';
import { ONBOARDING_V2 } from '../components/onboarding/onboardingV2Routes';
import {
  createCompanyForUser,
  type CreateCompanyResult,
} from '../lib/createCompany';
import { MARCA_POR_DEFECTO } from '../lib/altaDesdeCuestionario';
import { nombreDelUsuario } from '../lib/nombreUsuario';
import { RUBRO_SIN_DEFINIR } from '../lib/onboardingPendiente';
import { paisDelBrowser } from '../lib/paisDelBrowser';
import { isPaidPlan } from '../lib/planCapabilities';
import { ensureRebillSDK } from '../lib/rebill';
import RebillCardPayment, {
  type EstadoDelCheckoutRebill,
  type RebillCheckoutResult,
  type RebillPrefetchedCtx,
} from '../components/RebillCardPayment';

// 'datos' ya no existe: la empresa se crea sola al registrarse y el cliente llega al
// pago en un paso. Lo que ese paso preguntaba (marca y rubro) lo pregunta el
// cuestionario de onboarding DESPUÉS de cobrar.
// Ver ADR-2026-09-22-el-alta-por-link-cobra-primero-y-pregunta-despues.
type Step = 'loading' | 'auth' | 'creando' | 'pago' | 'exito' | 'error-estado';

// A dónde va tras pagar: al CUESTIONARIO de onboarding. La empresa se creó sola para
// poder cobrar en un paso, así que acá es donde se le pregunta lo que no se dedujo
// (rol, rubro, tipo de contenido) y donde genera su primera imagen — el mismo
// cuestionario que hace todo el mundo, sólo que después de pagar y no antes.
// Ver ADR-2026-09-22-el-alta-por-link-cobra-primero-y-pregunta-despues.
const POST_PAY_DEST = ONBOARDING_V2.setup;

// A dónde se manda a quien YA tiene plan pago y vuelve al link: a la app, no al
// cuestionario (si le falta, lo manda ahí la app sola).
const DEST_YA_CLIENTE = '/interfaz-v2';

/**
 * Altas de empresa EN VUELO, por usuario. Vive a nivel de módulo y no en un `useRef`
 * porque el árbol de rutas entero se remonta cuando la sesión resuelve —la `key` de
 * `ChatAtlasProvider` pasa de `'anon'` al id del usuario (`EntornoChatAtlas.tsx`)— y un
 * ref se estrena con la instancia nueva, así que no sirve de cerrojo. Es el mismo
 * motivo por el que el guard del embudo es `esPrimeraVezEnEstaCarga` y no un ref.
 *
 * Qué pasaba sin esto: `handleAuth` arranca la creación en la instancia A; el
 * `SIGNED_IN` del `signUp` remonta; la instancia B monta, lee el perfil —que todavía no
 * tiene empresa, porque el POST de A sigue en vuelo— y la crea de nuevo. Dos POSTs, dos
 * updates de `profiles` y dos `ensureDefaultBrand` por cada alta. Hoy no rompe datos,
 * pero de casualidad: el backend toma `FOR UPDATE` del perfil (una empresa, un solo
 * `onboarding_finalize`) y `brand_profiles` tiene el índice único parcial
 * `brand_profiles_one_default_per_company`, así que la segunda marca falla adentro de un
 * `try/catch` mudo. En la pantalla que cobra, depender de dos protecciones accidentales
 * de la base no alcanza.
 *
 * Es un cerrojo de EN VUELO y no un "una sola vez por carga": si la creación falla, la
 * entrada se borra, porque si no el botón de reintentar no tendría nada que reintentar.
 */
const altasEnVuelo = new Map<string, Promise<CreateCompanyResult>>();

// El `?pais=` que manda la landing se lee SIEMPRE por acá. Lo necesitan tres momentos
// distintos (el primer render, el montaje con sesión y el login) y cada lectura suelta
// era una oportunidad más de que uno se olvidara: el bug del país fue exactamente eso.
// Ver ADR-2026-09-22-el-pais-del-alta-se-resuelve-en-un-solo-lugar.
const codigoPaisDeLaUrl = (): string | null =>
  new URLSearchParams(window.location.search).get('pais');

/** Lo que el alta necesita del usuario para crear la empresa sin volver a preguntar. */
type UsuarioDelAlta = {
  id: string;
  email?: string | null;
  user_metadata?: Record<string, unknown> | null;
};

// Decide el paso inicial según el estado de la cuenta. `'redirect'` = la cuenta
// YA tiene un plan pago → no mostramos el pago (evita re-cobro si vuelve al link)
// y la mandamos a la app.
//
// FALLA CERRADO (`'error'`): si alguna de las dos consultas falla, NO se sigue al
// pago. `.maybeSingle()` no tira —devuelve `{ data: null, error }`— así que sin
// mirar el `error` un fallo de red o de RLS se leía como "cuenta nueva sin plan"
// y habilitaba el cobro: para un cliente de Stripe con suscripción activa eso es
// una SEGUNDA suscripción, con la vieja todavía cobrando (ADR-661). El mismo
// fallo en la consulta de `profiles` mandaba a cargar los datos de la empresa de
// nuevo, con riesgo de duplicarla.
//
// Devuelve además el país YA guardado: cuando el paso resuelto es 'pago' se
// saltea 'datos', que es el único lugar donde el `CountrySelect` setea `pais`.
// Sin esto, un cliente que vuelve al checkout sin `?pais=AR` se queda con el
// país vacío y cae al camino del exterior (Stripe/USD).
// Ver ADR-2026-08-18-alta-empresa-pais-solo-de-la-url.
//
// Lo que devuelve NO se aplica a mano en ningún camino: lo traduce
// `resolverPantallaDelAlta` y lo aplica entero `aplicarEstadoDeCuenta`. El tipo vive
// con el traductor (`lib/altaEmpresaPantalla.ts`), así que sumarle un campo obliga a
// pasar por el único lugar que decide qué ve la pantalla.
async function resolveStepForUser(
  userId: string,
): Promise<EstadoDeCuentaDelAlta> {
  const { data: profile, error: errorProfile } = await supabase
    .from('profiles')
    .select('id_company, pais')
    .eq('id', userId)
    .maybeSingle();
  // `yaFueClientePago: true` en los caminos que NO llegan a leer la empresa: es el
  // valor que no promete descuento, y acá se falla cerrado por definición.
  if (errorProfile)
    return { step: 'error', paisGuardado: null, yaFueClientePago: true };
  const perfil = profile as {
    id_company?: string | null;
    pais?: string | null;
  } | null;
  const companyId = perfil?.id_company;
  // Sin empresa todavía = cuenta que se está creando en este mismo flujo: nunca pagó.
  if (!companyId)
    return {
      step: 'datos',
      paisGuardado: perfil?.pais ?? null,
      yaFueClientePago: false,
    };
  const { data: company, error: errorEmpresa } = await supabase
    .from('company_profiles')
    .select('plan, pais, paid_plan_start_date')
    .eq('id_company', companyId)
    .maybeSingle();
  // `!company` además del error: `maybeSingle()` devuelve `data: null` sin error
  // cuando no hay fila, y ahí tampoco se sabe si la cuenta tiene plan.
  if (errorEmpresa || !company)
    return { step: 'error', paisGuardado: null, yaFueClientePago: true };
  const empresa = company as {
    plan?: string | null;
    pais?: string | null;
    paid_plan_start_date?: string | null;
  } | null;
  // El de la empresa manda: es el que se eligió para ESTA cuenta. El del perfil
  // queda de respaldo para empresas viejas creadas sin país.
  const paisGuardado = empresa?.pais ?? perfil?.pais ?? null;
  const yaFueClientePago = !!empresa?.paid_plan_start_date;
  if (isPaidPlan(empresa?.plan))
    return { step: 'redirect', paisGuardado, yaFueClientePago };
  return { step: 'pago', paisGuardado, yaFueClientePago };
}

export const EnterpriseCheckout = () => {
  const navigate = useNavigate();
  const { plan: planSlug } = useParams<{ plan: string }>();
  // Sólo para el destino post-pago (`destinoPostPago`), igual que `/checkout`. Sin
  // sesión no consulta nada; con sesión lee de la cache de localStorage primero.
  const { isDevTester: userGroupDevTester } = useUserGroup();

  const planId = planSlug ? ENTERPRISE_PLAN_SLUGS[planSlug] : undefined;
  const planCatalogo = planId ? getPlanById(planId) : undefined;

  // Plan a medida (ADR-662): si el slug no es uno de los públicos, puede ser un plan negociado
  // con un cliente puntual. Se le pregunta al payment server, que resuelve nombre + cuota de
  // `planes_custom` y el precio del Price de Stripe en vivo.
  const [planCustom, setPlanCustom] = useState<PlanCustomPublico | null>(null);
  // Arranca cargando SOLO si hace falta consultar: un slug del catálogo se resuelve sincrónico
  // y no debe mostrar spinner ni esperar a la red.
  const [planCustomEstado, setPlanCustomEstado] = useState<
    'no-aplica' | 'cargando' | 'ok' | 'not-found' | 'error'
  >(planCatalogo || !planSlug ? 'no-aplica' : 'cargando');

  useEffect(() => {
    if (planCatalogo || !planSlug) return;
    let cancelado = false;
    (async () => {
      try {
        const p = await fetchPlanCustom(planSlug);
        if (cancelado) return;
        setPlanCustom(p);
        setPlanCustomEstado('ok');
      } catch (err) {
        if (cancelado) return;
        // Un link válido con Stripe caído NO debe decir "link inválido": son mensajes
        // distintos porque las acciones del cliente son distintas (reintentar vs avisarnos).
        setPlanCustomEstado(
          err instanceof PlanCustomNoDisponible && err.tipo === 'not_found'
            ? 'not-found'
            : 'error',
        );
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [planSlug, planCatalogo]);

  const [step, setStep] = useState<Step>('loading');
  /**
   * ¿La cuenta ya fue cliente pago alguna vez? Arranca en `true` —el valor que NO
   * promete descuento— y lo baja `resolveStepForUser` cuando confirma que no.
   * Mientras tanto la pantalla está en `'loading'`, así que este default no se ve;
   * existe para que un camino nuevo que se olvide de setearlo falle hacia el lado
   * seguro. Ver ADR-2026-08-19-alta-por-link-respeta-la-regla-del-cupon.
   */
  const [yaFueClientePago, setYaFueClientePago] = useState(true);
  // Contexto del cobro ya resuelto al crear la empresa (empresa, usuario, mail, nombre),
  // para que `RebillCardPayment` no repita getUser + profiles. `undefined` = que lo
  // consulte él (quien vuelve al link con la empresa ya creada).
  // Ver ADR-2026-10-16-pago-directo-espera-y-medicion.
  const [ctxDelCobro, setCtxDelCobro] = useState<RebillPrefetchedCtx | undefined>(undefined);
  // Inputs del registro, para llevar el foco al que falta (validación propia, ver
  // `primerCampoInvalido`), y el contenedor del paso en mobile, para bajar hasta el
  // formulario de pago cuando Rebill está listo.
  const camposRef = useRef<Partial<Record<CampoDelRegistro, HTMLInputElement | null>>>({});
  // El aviso va DEBAJO del campo y no en el banner de arriba: con el teclado abierto en
  // un iPhone chico, el banner queda fuera de la vista y el toque parecería mudo de nuevo.
  const [avisoDeCampo, setAvisoDeCampo] = useState<{
    campo: CampoDelRegistro;
    mensaje: string;
  } | null>(null);
  const pasoRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  // OJO: este useState vivía más abajo, después de los `return` tempranos de "plan inválido".
  // Funcionaba de casualidad porque aquel guard era estable durante todo el montaje. El guard
  // de "cargando plan a medida" NO lo es (pasa a 'ok' en el mismo montaje), así que dejarlo
  // abajo rompía las reglas de hooks: React vería más hooks en el segundo render y crashearía.
  const [stripeLoading, setStripeLoading] = useState(false);
  // "Ver qué incluye" (resumen compacto colapsable del plan, en mobile).
  const [showFeatures, setShowFeatures] = useState(false);
  // Cupón "Hot Sale" (el MISMO que el checkout): descuento del primer mes que se
  // auto-aplica y se pasa a Rebill, para que el precio de /alta-empresa matchee
  // el de la landing (evita el mismatch "el primer mes" vs precio full).
  const [coupon, setCoupon] = useState<{
    codigo: string;
    multiplicador: number;
  } | null>(null);
  // ¿Ya sabemos si hay cupón o no? El paso de pago de Argentina NO monta
  // `RebillCardPayment` hasta que esto sea `true`: si montara con `couponCode: null` y
  // el cupón resolviera después, ese cambio de prop está en las deps del effect de
  // montaje del componente y DESTRUYE y RECREA el `<rebill-checkout>` —toda la init de
  // Rebill de nuevo— con los campos que el cliente ya tipeó adentro. Mismo gate que
  // `Checkout.tsx`, por el mismo motivo.
  //
  // "No hay cupón" es una respuesta, no una espera: esto se pone en `true` también
  // cuando el RPC falla y cuando el link pide precio de lista (`?desc=0`). Un gate que
  // falla cerrado acá significa no poder cobrar nunca.
  const [couponResolved, setCouponResolved] = useState(false);

  // ── Auth ──
  const [authMode, setAuthMode] = useState<'signup' | 'signin'>('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  // El país vigente, leído por `crearEmpresaYCobrar` sin entrar en sus deps: si entrara,
  // el callback cambiaría de identidad con cada resolución de país y re-suscribiría el
  // listener de `SIGNED_IN` que lo usa.
  //
  // Espeja a `pais` SIEMPRE, y por eso se asigna en el render y no sólo en los dos
  // `setPais`: el registro por mail llama a `crearEmpresaYCobrar` directo, sin pasar por
  // el puente, y con el ref arrancando vacío se perdía el `?pais=AR` que mandó la
  // landing — o sea, un argentino creando la empresa con país "Otro".
  const paisDeLaPantalla = useRef('');
  // Con qué método se entró por última vez EN ESTE DISPOSITIVO. No es un dato de la
  // cuenta: lo escribe `iniciarSeguimientoUltimoLogin` (main.tsx) en cada login de
  // cualquier pantalla. Sirve para avisarle a quien se registró con Google y vuelve por
  // este link a tipear una contraseña que nunca creó — un callejón sin salida que esta
  // pantalla no tenía antes de ofrecer Google.
  // Ver ADR-2026-08-11-ultimo-metodo-de-ingreso-se-registra-global.
  const [ultimoLogin] = useState(() => leerUltimoLogin());

  // ── Datos de la empresa ──
  // Nombre de la persona. Antes lo pedía el paso de datos; ahora se pide en el
  // registro, porque es el único dato de ese paso que no se puede deducir y porque el
  // cuestionario post-pago tampoco lo pregunta (lo toma de la metadata del signUp).
  const [name, setName] = useState('');
  // País: se inicializa con lo que manda la landing en la URL
  // (`?pais=CL`) para poder mostrar el precio en la moneda correcta desde el
  // PRIMER render y dejar el select ya elegido. Si no viene o es un código
  // desconocido, queda vacío: el usuario lo elige y el precio se muestra en
  // USD hasta entonces, que es el fallback correcto. Ver ADR-592.
  const [pais, setPais] = useState(() => paisInicialDelAlta(codigoPaisDeLaUrl()));
  // El espejo del ref de arriba. Va acá, pegado al estado que copia, y no adentro de un
  // effect: `crearEmpresaYCobrar` puede correr en el mismo turno en que el país se
  // resolvió, y un effect llegaría tarde.
  paisDeLaPantalla.current = pais;
  // El param puede faltar o venir 'OTHER' (la geo de la IP se equivoca: pasó con
  // el proxy del navegador in-app de Instagram). En ese caso el país lo pone el
  // effect de abajo, con el que el cliente ya eligió a mano.

  // Precarga del SDK de Rebill apenas el país es Argentina: los chunks bajan
  // mientras el cliente completa los pasos previos (cuenta/datos), en vez de
  // esperar a que `RebillCardPayment` monte en el paso de pago. Un fallo acá no
  // molesta: el componente vuelve a llamar y maneja el error (`ensureRebillSDK`
  // resetea su promesa al fallar). Se dispara también para planes a medida AR
  // (que cobran por Stripe): bajar el chunk de más es más barato que enhebrar
  // `esCustom` hasta acá arriba de los return tempranos.
  // Ver ADR-2026-08-24-checkout-rebill-carga-silenciosa.
  useEffect(() => {
    if (paisFlags(pais).isArgentina) void ensureRebillSDK().catch(() => {});
  }, [pais]);

  // Persiste el país que el cliente eligió a mano en la red de seguridad del pago.
  // Toca la empresa Y el perfil, que es lo mismo que hace `createCompanyForUser`.
  const guardarPaisElegido = useCallback(async (elegido: string) => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;
      await supabase.from('profiles').update({ pais: elegido }).eq('id', user.id);
      const { data: perfil } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();
      const idCompany = (perfil as { id_company?: string | null } | null)?.id_company;
      if (idCompany) {
        await supabase
          .from('company_profiles')
          .update({ pais: elegido })
          .eq('id_company', idCompany);
      }
    } catch {
      /* best-effort: el cobro usa el país de la pantalla, que ya quedó bien */
    }
  }, []);

  // Crea la empresa y manda al pago, SIN preguntar nada. Es el reemplazo del paso de
  // "datos" que había entre la cuenta y el pago: pedía nombre, país, marca y rubro, y
  // era lo único que separaba a alguien decidido de poner la tarjeta.
  //
  // Los cuatro datos salen de donde ya están, igual que en el camino normal
  // (`altaDesdeCuestionario`):
  //   nombre → del registro (el campo del form, o el perfil de Google)
  //   marca  → `MARCA_POR_DEFECTO`; la cambia después desde la app
  //   país   → el `?pais=` de la landing, con la heurística del browser de respaldo.
  //            OJO: el backend PISA esto con el país que deduce de la IP
  //            (`paisFinal = paisPorIp ?? body.pais ?? profile.pais`), así que esto es
  //            el respaldo para cuando el servicio de geo no contesta.
  //   rubro  → el centinela: todavía no se preguntó. Lo pisa el cuestionario post-pago.
  //
  // Falla RUIDOSO: si la empresa no se puede crear, NO se muestra el formulario de pago.
  // Cobrarle a alguien sin empresa deja el webhook sin `id_company` y el pago huérfano.
  // Ver ADR-2026-09-22-el-alta-por-link-cobra-primero-y-pregunta-despues.
  const crearEmpresaYCobrar = useCallback(
    async (user: { id: string; email?: string | null; user_metadata?: Record<string, unknown> | null }) => {
      setStep('creando');
      setError(null);
      try {
        // El cerrojo de `altasEnVuelo` es lo que hace que esto salga UNA vez aunque la
        // pantalla se remonte en el medio.
        let enVuelo = altasEnVuelo.get(user.id);
        if (!enVuelo) {
          enVuelo = createCompanyForUser({
            userId: user.id,
            name: nombreDelUsuario(user),
            pais: paisDeLaPantalla.current || paisDelBrowser() || 'Otro',
            companyName: MARCA_POR_DEFECTO,
            industry: RUBRO_SIN_DEFINIR,
            // Cierra el linkeo empresa ⟷ sesión anónima (el `sid` que mandó la landing).
            sessionId: new URLSearchParams(window.location.search).get('sid'),
            // Acá la empresa se crea para cobrarle en el acto: no empieza ninguna prueba,
            // así que no es un `StartTrial` para Meta.
            inicioDePrueba: false,
            // Lo accesorio (rubro, atribución, marca default) se completa mientras la
            // persona mira el formulario: el cobro no lo usa.
            // Ver ADR-2026-10-16-pago-directo-espera-y-medicion.
            extrasEnSegundoPlano: true,
          });
          altasEnVuelo.set(user.id, enVuelo);
          // Sólo los FALLOS liberan el cerrojo: si salió bien, la entrada queda y una
          // segunda llamada devuelve el mismo resultado sin volver a pegarle al backend.
          // Si falló, se borra para que el botón de reintentar pueda salir de verdad.
          void enVuelo
            .then((r) => {
              if (!r.ok) altasEnVuelo.delete(user.id);
            })
            .catch(() => altasEnVuelo.delete(user.id));
        }
        const res = await enVuelo;
        if (!res.ok) throw new Error(res.error || 'No se pudo crear la empresa.');
        // Lo que `RebillCardPayment` iría a buscar solo (getUser + profiles): ya lo
        // sabemos. Son dos consultas menos antes de que se pueda tipear la tarjeta.
        if (res.companyId) {
          const fullName = nombreDelUsuario(user).trim();
          setCtxDelCobro({
            companyCtx: { idCompany: res.companyId, userId: user.id },
            customerPrefill:
              user.email || fullName
                ? {
                    ...(user.email ? { email: user.email } : {}),
                    ...(fullName ? { fullName } : {}),
                  }
                : null,
          });
        }
        setStep('pago');
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'No se pudo preparar tu cuenta.',
        );
        // NO vuelve a `'auth'`. Esta persona ya está logueada: mostrarle "Creá tu
        // cuenta" la manda a registrarse de nuevo, chocar con "ese mail ya tiene
        // cuenta" y tener que iniciar sesión para volver al mismo lugar. `error-estado`
        // es la pantalla que ya existe para "no se pudo, probá de nuevo", y su botón
        // recarga — que acá es exactamente el reintento correcto: remonta, `getUser`
        // ve la sesión sin empresa y vuelve a intentar la creación.
        setStep('error-estado');
      }
    },
    [],
  );

  // ÚNICO puente entre "qué es esta cuenta" y "qué muestra la pantalla". Los caminos
  // que llegan al pago con una cuenta que YA existe —el montaje con sesión abierta y el
  // login— entran los dos por acá, y aplican el objeto ENTERO que devuelve
  // `resolverPantallaDelAlta`: paso, país e historial de pago juntos.
  //
  // No es prolijidad: el login destructuraba `{ step, yaFueClientePago }` y tiraba
  // `paisGuardado`, así que a un argentino con cuenta previa se le ofrecía Stripe en USD
  // en vez de Rebill en ARS. Eran tres call sites que había que acordarse de mantener
  // iguales; ahora hay uno solo, y `lib/altaEmpresaPantalla.test.ts` cuenta que siga
  // habiendo uno solo.
  // Ver ADR-2026-09-22-el-pais-del-alta-se-resuelve-en-un-solo-lugar.
  //
  // El `?pais=` de la landing manda cuando es un código conocido: es el que el cliente
  // está mirando y no se le puede cambiar por debajo. Si no vino o vino `OTHER` (el
  // proxy del navegador in-app de Instagram), cae al país de la cuenta. OJO con ese
  // fallback: `company_profiles.pais` lo escribe el backend priorizando la geo de la IP
  // (`paisPorIp ?? body.pais ?? profile.pais`), así que NO es necesariamente lo que la
  // persona eligió en el select. Igual es mejor que vacío, que manda a Stripe/USD.
  // Re-resolver acá es idempotente: mismo criterio que el estado inicial.
  //
  // `sigueVigente` es el guard del effect de montaje (StrictMode monta dos veces y el
  // usuario puede irse antes de que vuelva la consulta). Default `true` para el camino
  // del login, donde el submit ya terminó y no hay nada que cancelar.
  const aplicarEstadoDeCuenta = useCallback(
    async (user: UsuarioDelAlta, sigueVigente: () => boolean = () => true) => {
      const pantalla = resolverPantallaDelAlta(
        await resolveStepForUser(user.id),
        codigoPaisDeLaUrl(),
      );
      if (!sigueVigente()) return;
      // El orden es el del código que reemplaza, y no es cosmético: en el camino
      // `redirect` la pantalla se desmonta, así que NO se le toca el país. Setearlo
      // antes de navegar obligaba a razonar sobre si React batchea el `navigate` con
      // los `setState` para saber si se dispara la precarga del SDK de Rebill para
      // alguien a quien ya no se le va a cobrar. En la pantalla que cobra, la versión
      // que no hay que razonar es mejor que la que se defiende.
      setYaFueClientePago(pantalla.yaFueClientePago);
      if (pantalla.paso === 'redirect') {
        navigate(DEST_YA_CLIENTE, { replace: true });
        return;
      }
      setPais(pantalla.pais);
      // Cuenta sin empresa: antes se le mostraba el formulario de datos. Ahora la
      // empresa se crea sola y el cliente ve el pago. Cubre las dos formas de llegar
      // acá: la cuenta que se acaba de crear y la que ya existía sin empresa.
      if (pantalla.paso === 'datos') {
        await crearEmpresaYCobrar(user);
        return;
      }
      // Entrar a `error-estado` por ACÁ es "no se pudo leer el estado de la cuenta": el
      // cartel tiene que ser el de la puerta que no cobra, no un error viejo del
      // formulario que quedó colgado en el estado.
      if (pantalla.paso === 'error-estado') setError(null);
      setStep(pantalla.paso);
    },
    [navigate, crearEmpresaYCobrar],
  );

  // Al montar: si ya hay sesión, arrancar en el paso que corresponda.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (cancelled) return;
      if (!user) {
        setStep('auth');
        return;
      }
      await aplicarEstadoDeCuenta(user, () => !cancelled);
    })();
    return () => {
      cancelled = true;
    };
  }, [aplicarEstadoDeCuenta]);

  // Tracking del embudo (app-side, best-effort) en `alta_empresa_clicks`. Taxonomía
  // del PDF de tracking (jul-2026). Cada evento lleva el `session_id` + UTMs que
  // mandó la landing en la URL → cierra el linkeo anónimo→pago (antes se contaba
  // sin linkear, ADR-593/594):
  //  · 'plan_click'    → llegada al link (todos, antes de elegir signup/signin).
  //  · 'signup'        → el usuario SE REGISTRA (solo signup, NO signin).
  //  · 'checkout_view'    → ENTRÓ al paso de pago (vio el form, NO que pagó).
  //  · 'checkout_created' → se creó la Checkout Session de Stripe (solo EXTERIOR,
  //                         justo antes del redirect). AR/Rebill no lo emite (in-app).
  //  · 'pago_ok'          → cobró (client-side, SOLO Argentina/Rebill). TRANSITORIO: el
  //                      `payment_confirmed` autoritativo (todos los proveedores) lo
  //                      va a escribir el server de pagos server-side (pendiente).
  //  · 'checkout_listo'   → el form de Rebill está listo para tipear. metadata:
  //                         ms_desde_registro, ms_desde_pago_visible.
  //  · 'checkout_lento'   → pasaron 15 s sin `ready` de Rebill.
  //  · 'checkout_roto'    → Rebill emitió `ready` con el DOM vacío.
  //  · 'tarjeta_enviada'  → primer submit del form de Rebill. metadata: ms_desde_listo.
  //                         Contra `payment_events`: la diferencia son tarjetas que el
  //                         SDK frenó antes de mandarlas.
  //  · 'pago_rechazado_form' → el SDK emitió `error`. metadata: mensaje.
  //  `signup` lleva ms_registro y `checkout_view` ms_desde_registro. Todo evento va
  //  también a PostHog como `alta_<evento>`. Ver ADR-2026-10-16-pago-directo-espera-y-medicion.
  //  · 'checkout_bloqueado' → NO se pudo leer el estado de la cuenta y la pantalla frenó
  //                      el cobro para no duplicar una suscripción. Es una puerta que le
  //                      niega el pago a un cliente y no dejaba ningún rastro: si le
  //                      pasaba a alguien, no nos enterábamos nunca.
  // OJO: 'payment_confirmed' lo escribe el SERVER de pagos en esta misma tabla, y
  // 'click'/'conversion' son la taxonomía vieja (retirada el 31/07). No reusar esos tres.
  // `source` = ?src (pricing/directo). Ver ADR-593 y ADR-594.
  const trackEvent = useCallback(
    (
      event:
        | 'plan_click'
        | 'signup'
        | 'checkout_view'
        | 'checkout_created'
        | 'pago_ok'
        | 'checkout_bloqueado'
        // Medición de la espera y del recuadro de Rebill
        // (ADR-2026-10-16-pago-directo-espera-y-medicion):
        | 'checkout_listo'
        | 'checkout_lento'
        | 'checkout_roto'
        | 'tarjeta_enviada'
        | 'pago_rechazado_form',
      metadata?: Record<string, string | number | null>,
    ) => {
      if (!planSlug) return;
      const params = new URLSearchParams(window.location.search);
      const source = params.get('src') || 'directo';
      // Atribución que mandó la landing (?sid, ?utm_*, ?referrer): cierra el linkeo
      // anónimo→pago. La landing genera el session_id y lo pasa en el redirect (no
      // escribe en la base); acá lo persistimos en CADA evento del embudo.
      const sessionId = params.get('sid');
      // Los CINCO UTM que manda la landing, no tres. Hasta el 2026-09-22 la tabla no
      // tenía columnas para `utm_medium` ni `utm_term` y se tiraban: de las 114 llegadas
      // a esta pantalla en 90 días, 59 traían UTMs y LAS 59 traían `utm_medium`. O sea
      // que el embudo no podía separar `ig/paid` de `ig/social` aunque la URL lo decía
      // —y `profiles` sí los guardaba, así que el dato entraba a la app y se perdía sólo
      // acá. Va también `utm_id`, el id de campaña de Meta.
      const utmSource = params.get('utm_source');
      const utmMedium = params.get('utm_medium');
      const utmCampaign = params.get('utm_campaign');
      const utmTerm = params.get('utm_term');
      const utmContent = params.get('utm_content');
      const utmId = params.get('utm_id');
      // El `?referrer=` de la landing nunca llegó: 0 de 575 filas desde julio de 2026.
      // Se sigue leyendo primero por si algún día lo manda, pero lo que de verdad llena
      // la columna es el referrer de la llegada — el único dato de origen que queda para
      // el 48% de las visitas que entran sin ningún UTM. Sale del helper y no de
      // `document.referrer` acá para que la vuelta del OAuth no lo pise con
      // `accounts.google.com`.
      const referrer = params.get('referrer') || referrerDeLlegada();
      // Separa el testeo del tráfico real (localhost/preview, o ?test=1 en prod) y
      // TAMBIÉN los bots: los crawlers entran a estos links todos los días y estaban
      // contados como personas en el techo del embudo (Bytespider sumó 12 llegadas en
      // 3 días). Ver `esTraficoAutomatico`, que es conservador a propósito.
      const isTest =
        /localhost|127\.0\.0\.1|\.pages\.dev|\.vercel\.app/i.test(
          window.location.hostname,
        ) ||
        params.get('test') === '1' ||
        esTraficoAutomatico(navigator.userAgent);
      // Espejo en PostHog: así cada paso del embudo (y sus tiempos) aparece en la línea
      // de tiempo de la grabación y se pueden armar embudos sin SQL. La fuente para
      // cruzar con pagos sigue siendo `alta_empresa_clicks`.
      // Ver ADR-2026-10-16-pago-directo-espera-y-medicion.
      trackEventEnPostHog(`alta_${event}`, {
        plan: planSlug,
        source,
        is_test: isTest,
        ...(metadata ?? {}),
      });
      // Devuelve la promesa del insert (no la desacopla) para que un caller que
      // esté por navegar cross-origin (redirect a Stripe) pueda ESPERARLA y no
      // perder el evento. Los demás call sites la ignoran con `void`.
      return (async () => {
        try {
          // `id_company` se resuelve en el momento del evento, no se cachea. Queda
          // en null en 'plan_click' Y en 'signup' (en plan_click no hay sesión; en
          // signup la hay pero la empresa se crea DESPUÉS, en `handleDatos`); se
          // puebla recién desde 'checkout_view'. No se rellena a posteriori: la RLS
          // es solo INSERT. Antes esto importaba porque se contaba SIN linkear
          // (ADR-593/594); ahora el `session_id` linkea el embudo entero, así que un
          // evento sin id_company igual se ata al pago por su session_id.
          let idCompany: string | null = null;
          try {
            const {
              data: { user },
            } = await supabase.auth.getUser();
            if (user) {
              const { data } = await supabase
                .from('profiles')
                .select('id_company')
                .eq('id', user.id)
                .single();
              idCompany = ((data as any)?.id_company as string) ?? null;
            }
          } catch {
            /* sin sesión o sin perfil todavía → queda null */
          }
          await supabase.from('alta_empresa_clicks').insert({
            plan: planSlug,
            source,
            event,
            id_company: idCompany,
            session_id: sessionId,
            utm_source: utmSource,
            utm_medium: utmMedium,
            utm_campaign: utmCampaign,
            utm_term: utmTerm,
            utm_content: utmContent,
            utm_id: utmId,
            referrer,
            user_agent: navigator.userAgent,
            is_test: isTest,
            metadata: metadata ?? null,
          });
        } catch {
          /* best-effort, no bloquea el alta */
        }
      })();
    },
    [planSlug],
  );

  // El guard contra el doble evento NO puede ser un `useRef`: el árbol de rutas entero
  // se remonta cuando resuelve la sesión (la `key` de `ChatAtlasProvider` pasa de
  // `'anon'` al id del usuario) y el remonte estrena los refs. Por eso en producción
  // hay pares de `plan_click` separados por 34 ms — 5 en toda la historia de la tabla,
  // todos de llegadas ya logueadas, que son las únicas que disparan el remonte.
  // `esPrimeraVezEnEstaCarga` vive a nivel de MÓDULO, que es lo único que el remonte no
  // reinicia, y muere con el documento: el que vuelve mañana cuenta de nuevo.
  // (StrictMode sigue prendido y también quedaba cubierto por el ref; el cerrojo lo
  // cubre igual.) Ver ADR-2026-09-22-el-embudo-del-alta-por-link-deja-de-contar-bots-y-remontes.
  useEffect(() => {
    // `planId` es el id del CATÁLOGO, y es `undefined` para un plan a medida: pedirlo
    // acá dejaba a esos links sin techo de embudo (emitían signup, checkout_view y
    // pago_ok, pero nunca la llegada). Se espera a que el plan esté RESUELTO —del
    // catálogo o a medida— para no contar como llegada un slug que no existe.
    if (!planSlug) return;
    if (!planId && !planCustom) return;
    if (!esPrimeraVezEnEstaCarga(`plan_click:${planSlug}`)) return;
    void trackEvent('plan_click');
  }, [planId, planSlug, planCustom, trackEvent]);

  // 'conversion' = ENTRÓ al paso de pago. OJO: no significa que pagó — para eso
  // está `pago_ok`.
  //
  // Va atado al ESTADO y no al camino, a propósito. Antes se emitía dentro de
  // `handleDatos`, que es solo UNO de los tres caminos que llegan a 'pago':
  //
  //   1. handleDatos           → cuenta nueva, o cuenta sin empresa
  //   2. signIn                → `resolveStepForUser` devuelve 'pago' directo
  //   3. el effect de montaje  → vuelve con sesión abierta y empresa ya creada
  //
  // Los caminos 2 y 3 son justamente los del cliente que YA tenía cuenta con
  // empresa, que nunca pasa por `handleDatos` y por lo tanto no registraba
  // conversión. Sacar el gate de `didSignupRef` no alcanzaba: el problema no era
  // el gate sino el lugar. Ver ADR-594.
  //
  // El cerrojo acá es DEFENSIVO, no un arreglo: en 365 días no hay un solo
  // `checkout_view` duplicado (el mínimo entre dos del mismo sid+plan es 2m17s),
  // porque la sesión resuelve antes que `resolveStepForUser`. Cierra la carrera en vez
  // de confiar en que la siga ganando.
  // Parámetros del plan para los eventos de Meta (`lib/metaPixel.ts`). Se arman acá y no
  // a partir de `price` (más abajo) porque entre medio hay `return`s tempranos: un efecto
  // que cerrara sobre un `const` que ese render no llegó a inicializar reventaría. Sólo
  // usa lo que ya está resuelto a esta altura.
  const parametrosMetaDelPlan = useCallback(
    () =>
      planCatalogo
        ? parametrosDePlan(
            planCatalogo.id,
            getPlanPriceForPais(planCatalogo.id, planCatalogo.price, pais),
            currencyForPais(pais),
          )
        : parametrosDePlan(
            planSlug ?? 'custom',
            planCustom ? montoDePrecioCustom(planCustom.precio, planCustom.moneda) : null,
            planCustom?.moneda ?? null,
          ),
    [planCatalogo, pais, planSlug, planCustom],
  );

  useEffect(() => {
    if (step !== 'pago') return;
    if (!planSlug || !esPrimeraVezEnEstaCarga(`checkout_view:${planSlug}`)) return;
    void trackEvent('checkout_view', {
      ms_desde_registro: msDesde('registro_enviado'),
    });
    marcarMomento('pago_visible');
    // Meta: llegó al formulario de pago con el plan elegido (el mismo cerrojo de arriba
    // ya garantiza una vez por plan y carga).
    trackMeta('InitiateCheckout', parametrosMetaDelPlan());
  }, [step, planSlug, trackEvent, parametrosMetaDelPlan]);

  // La puerta que FRENA el cobro cuando no se pudo leer el estado de la cuenta no
  // dejaba ningún rastro: si le pasaba a un cliente, no nos enterábamos. Es el único
  // evento del embudo que cuenta un problema NUESTRO, no una decisión del usuario.
  useEffect(() => {
    if (step !== 'error-estado') return;
    if (!planSlug || !esPrimeraVezEnEstaCarga(`checkout_bloqueado:${planSlug}`)) return;
    void trackEvent('checkout_bloqueado');
  }, [step, planSlug, trackEvent]);

  // Auto-aplicar el cupón "Hot Sale" (descuento del primer mes), igual que el
  // checkout: mismo cupón por ID; si está activo/vigente y no agotado, se aplica
  // y se pasa a Rebill. Así el precio matchea la landing.
  useEffect(() => {
    // Descuento opcional: si la URL trae `?desc=0`, NO se aplica el cupón →
    // precio real. Sirve para que ventas mande un link a precio full cuando
    // quiera (ej. `alta-empresa/ultra-1?desc=0`). Sin el param → con descuento.
    //
    // Este return va ANTES del IIFE, así que el `finally` de abajo no corre: sin
    // marcar resuelto acá, un link a precio full se quedaría para siempre en el loader
    // del paso de pago, o sea sin poder pagar. Precio de lista es una respuesta.
    if (new URLSearchParams(window.location.search).get('desc') === '0') {
      setCouponResolved(true);
      return;
    }
    let cancelled = false;
    // Tope de tiempo: el gate del cupón bloquea el formulario de pago, así que un RPC
    // que NO responde (no que falla: que se queda colgado) dejaba al cliente en
    // "Cargando…" para siempre, sin poder pagar. `supabase.rpc` no tiene timeout
    // propio y el cliente se crea sin `fetch` custom, así que el tope va acá. Al
    // vencerse se sigue sin cupón, que es la respuesta segura: precio de lista.
    // Mismo criterio que el `Promise.race` del tracking antes del redirect a Stripe.
    const topeDelCupon = window.setTimeout(() => {
      if (!cancelled) setCouponResolved(true);
    }, 6000);
    (async () => {
      try {
        // RPC security-definer: valida vigencia server-side y devuelve SOLO el
        // cupón del alta (no expone la tabla `cupones` al usuario anónimo, que es
        // quien está en el paso de crear cuenta). Ver SQL en el doc/ADR.
        const { data } = await supabase.rpc('get_alta_empresa_coupon');
        const row = Array.isArray(data) ? data[0] : data;
        if (cancelled || !row) return;
        setCoupon({
          codigo: row.codigo,
          multiplicador: Number(row.multiplicador),
        });
      } catch {
        /* sin cupón → precio full */
      } finally {
        // SIEMPRE se abre el gate, incluso si el RPC falló o no devolvió cupón: la
        // respuesta en ese caso es "precio de lista", no "esperá". (El `return` de
        // arriba, adentro del `try`, también pasa por acá.)
        window.clearTimeout(topeDelCupon);
        if (!cancelled) setCouponResolved(true);
      }
    })();
    return () => {
      cancelled = true;
      window.clearTimeout(topeDelCupon);
    };
  }, []);

  // Captura de atribución al montar: quien cae DIRECTO en esta pantalla con
  // ?utm_*/?ref= y cookies de Meta no pasa por /auth, así que capturamos acá
  // (ADR-613). Baja todo a localStorage; se persiste tras el signUp.
  useEffect(() => {
    captureAttributionParams();
  }, []);

  // ── Alta / ingreso con Google ────────────────────────────────────────────
  //
  // El 38% de las altas de la app son con Google y esta pantalla no lo ofrecía: el que
  // ya tiene cuenta de Google y llega por el link se topaba con un formulario de
  // contraseña que nunca creó.
  //
  // El OAuth SE VA del documento, así que `redirectTo` es lo único que trae de vuelta el
  // contexto: se vuelve a ESTA misma URL con su query entera (plan, `?pais=`, `?sid=`,
  // `?src=`, `?desc=`). Ver `urlDeVueltaDelAlta`.
  //
  // OJO — DEPENDENCIA DE CONFIGURACIÓN: la URL de retorno tiene que estar permitida en
  // Supabase (Authentication → URL Configuration → Redirect URLs, con un patrón que
  // cubra las rutas de alta-empresa). Si no lo está, Supabase NO falla: manda al Site
  // URL y el
  // cliente aparece logueado en la app, sin plan y sin haber pagado.
  // Ver ADR-2026-09-22-google-en-el-alta-por-link.
  //
  // Dentro de Instagram, Facebook y compañía, Google no siempre deja entrar: se muestra un
  // aviso con la salida, sin frenar el botón (ADR-2026-10-20-google-avisa-en-instagram-sin-frenar).
  // Acá importa doble: es la pantalla que cobra, y el link conserva plan, país y `sid`.
  const { embebido } = useAvisoGoogleEnApp('alta_empresa');

  const handleGoogle = async () => {
    setError(null);
    setGoogleLoading(true);
    try {
      const { error: err } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: urlDeVueltaDelAlta(
            window.location.origin,
            window.location.pathname,
            window.location.search,
          ),
        },
      });
      if (err) throw err;
      // Si no tiró, el browser ya está navegando a Google: no se baja el loading.
    } catch (err) {
      setError(mensajeDeErrorDeAuth(err));
      setGoogleLoading(false);
    }
  };

  // Volver con "atrás" desde la pantalla de Google: la página vuelve del bfcache con el
  // estado congelado y el botón seguiría girando para siempre — la promesa que iba a
  // bajar el flag quedó abandonada al congelarse el documento.
  // Ver ADR-2026-08-11-bfcache-boton-google-colgado.
  useBfcacheRestore(() => setGoogleLoading(false));

  // La VUELTA de Google. Dos cosas que el camino del mail resuelve en `handleAuth` y
  // este no, porque el `signUp` no pasa por nuestro código:
  //
  //  1. La ATRIBUCIÓN. `captureAttributionParams` ya bajó los UTMs y las cookies de Meta
  //     a localStorage al montar; acá se persisten en `profiles`. Mismo criterio de
  //     "alta fresca" que `Auth.tsx` (creada hace menos de 60s), porque un login normal
  //     no tiene que pisar la atribución del alta original.
  //  2. El evento `signup` del embudo, que si no se perdería para todo el que entra por
  //     Google.
  //
  // Y además aplica el estado de la cuenta: la sesión del OAuth llega en el hash y
  // `detectSessionInUrl` la procesa DESPUÉS del montaje, así que el `getUser()` del
  // effect de arriba puede no verla todavía. Hoy la pantalla igual se acomoda porque el
  // árbol de rutas se remonta al resolver la sesión, pero eso es un accidente que está
  // para irse: este listener hace que no dependa de él.
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((evento, session) => {
      if (evento !== 'SIGNED_IN' || !session) return;
      // El alta por mail ya la resuelve `handleAuth`; acá sólo los proveedores externos.
      if (session.user.app_metadata?.provider === 'email') return;
      void (async () => {
        const creado = session.user.created_at
          ? new Date(session.user.created_at).getTime()
          : 0;
        const esAltaFresca = creado > 0 && Date.now() - creado < 60_000;
        if (esAltaFresca) {
          await saveSignupAttribution(session.user.id);
          if (planSlug && esPrimeraVezEnEstaCarga(`signup:${planSlug}`)) {
            // La vuelta del OAuth es una carga nueva: la espera de nuestro lado se
            // mide desde acá.
            marcarMomento('registro_enviado');
            void trackEvent('signup');
          }
        }
        await aplicarEstadoDeCuenta(session.user);
      })();
    });
    return () => subscription.unsubscribe();
  }, [aplicarEstadoDeCuenta, planSlug, trackEvent]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    // Validación propia en vez de la del navegador: en Instagram el aviso nativo no se
    // ve claro y el toque parecía no hacer nada. Ver lib/registroDelAlta.ts.
    const invalido = primerCampoInvalido({ modo: authMode, name, email, password });
    if (invalido) {
      setError(null);
      setAvisoDeCampo(invalido);
      camposRef.current[invalido.campo]?.focus();
      return;
    }
    setAvisoDeCampo(null);
    // Cierra el teclado: lo que viene es una espera y después el formulario de pago,
    // que con el teclado abierto queda a medio ver en el celular.
    (document.activeElement as HTMLElement | null)?.blur?.();
    setError(null);
    setAuthLoading(true);
    try {
      if (authMode === 'signup') {
        // La hora del toque se toma acá, pero se MARCA recién con el registro hecho: si
        // el mail ya tenía cuenta (frecuente en este link) la persona termina entrando
        // por el login, y medir desde este intento fallido metería en la espera el rato
        // de leer el aviso y tipear la contraseña.
        const tocoCrearCuenta = Date.now();
        const { data, error: signUpErr } = await supabase.auth.signUp({
          email: email.trim(),
          password,
          // UTMs + Meta + ref en la metadata: los baja el trigger (spec 2026-09-11 §5.3).
          // `full_name` va junto —misma clave que usa `AuthForm`— porque es de donde lo
          // lee `nombreDelUsuario` cuando se crea la empresa, un instante después.
          options: {
            data: { ...metadataDeAtribucion(), full_name: name.trim() },
          },
        });
        // Mail que ya tiene cuenta: la respuesta correcta no es un cartel sino
        // llevarlo a iniciar sesión con el mail que ya escribió. Este proyecto NO pide
        // confirmar el mail, así que Supabase devuelve este error (y no la cuenta
        // ofuscada que devolvería con la confirmación activada): es un caso frecuente
        // acá, donde mucha gente que ya probó Atlas vuelve por el link a suscribirse.
        // Ver ADR-2026-09-22-los-errores-de-auth-del-alta-por-link-estan-en-castellano.
        if (signUpErr && esMailYaRegistrado(signUpErr)) {
          setAuthMode('signin');
          setPassword('');
          throw signUpErr; // el catch lo traduce con el texto que invita a entrar
        }
        if (signUpErr) throw signUpErr;
        if (!data.session) {
          // La cuenta ATLAS no pide confirmar mail, pero por las dudas.
          throw new Error('Revisá tu correo para confirmar la cuenta y volvé a entrar.');
        }
        // Cuenta recién creada: no puede tener `paid_plan_start_date`, así que le
        // corresponde el cupón. Sin esta línea el alta nueva —que es el público de
        // estos links— llega al paso de pago con el default `true` del `useState`, el
        // valor que NO promete descuento: la landing muestra el 30% y el form de Rebill
        // cobra precio de lista. Hoy no se nota porque el remonte del árbol de rutas
        // (la `key` de `ChatAtlasProvider`, que cambia en el SIGNED_IN del signUp)
        // vuelve a pasar por el effect de montaje y lo corrige de rebote; esta línea es
        // la que lo sostiene cuando ese remonte se saque.
        // Ver ADR-2026-09-22-el-pais-del-alta-se-resuelve-en-un-solo-lugar.
        setYaFueClientePago(false);
        // Registro exitoso (solo signup, NO signin) → evento 'signup' del embudo.
        marcarMomento('registro_enviado', tocoCrearCuenta);
        void trackEvent('signup', { ms_registro: msDesde('registro_enviado') });
        // Y derecho a crear la empresa y cobrar: no hay paso intermedio.
        await crearEmpresaYCobrar(data.session.user);
      } else {
        const { data: signInData, error: signInErr } =
          await supabase.auth.signInWithPassword({
            email: email.trim(),
            password,
          });
        if (signInErr) throw signInErr;
        if (!signInData.user) {
          throw new Error('No se pudo obtener la sesión. Reintentá.');
        }
        // Este camino aplicó mal el estado de la cuenta dos veces, y las dos costaron
        // plata: primero comparaba el objeto entero contra `'redirect'` (siempre falso)
        // y lo metía tal cual en `setStep`; después, ya destructurado, leía
        // `{ step, yaFueClientePago }` y TIRABA `paisGuardado` —el de quien ya tenía
        // cuenta, que es justo quien entra por el login— y sin país un argentino veía
        // Stripe en USD (ADR-2026-08-19-alta-por-link-respeta-la-regla-del-cupon,
        // ADR-2026-09-22-el-pais-del-alta-se-resuelve-en-un-solo-lugar).
        // Por eso acá ya no se elige nada: se aplica lo que resuelve el único puente.
        await aplicarEstadoDeCuenta(signInData.user);
      }
    } catch (err) {
      // Traducido: acá se mostraba el `message` crudo de Supabase y el cliente leía
      // "Invalid login credentials" en inglés, en el paso que cobra.
      setError(mensajeDeErrorDeAuth(err));
    } finally {
      setAuthLoading(false);
    }
  };

  // "¿Olvidaste tu contraseña?" SIN irse de la pantalla: mandarlo a `/auth` perdía el
  // `?pais=` y el `?sid=` de la URL, que son los que deciden la moneda del precio y el
  // linkeo del embudo. Mismo contrato que `AuthCompat`: el mail vuelve a
  // `/restablecer-clave`, y la respuesta es NEUTRA (no confirma si el mail existe).
  const [resetEnviado, setResetEnviado] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const pedirResetDeClave = async () => {
    const mail = email.trim();
    if (!mail) {
      setError('Escribí tu correo arriba y te mandamos el link para crear una contraseña nueva.');
      return;
    }
    setError(null);
    setResetLoading(true);
    // Dónde estaba, para volver acá después de cambiar la contraseña: el plan, el
    // `?pais=` que decide moneda y proveedor, el `?desc=` del cupón y el `?sid=` del
    // embudo. Sin esto `/restablecer-clave` termina en `/auth` pelado y se pierde todo.
    recordarVueltaDelReset(`${window.location.pathname}${window.location.search}`);
    try {
      await supabase.auth.resetPasswordForEmail(mail, {
        // Pelado a propósito: ver `lib/vueltaAlAlta.ts` (meter el destino acá depende de
        // la lista de Redirect URLs de Supabase, y equivocarse rompe el reset entero).
        redirectTo: `${window.location.origin}/restablecer-clave`,
      });
    } catch {
      /* La respuesta es neutra igual: un fallo acá no se le cuenta al usuario, que
         no puede hacer nada distinto. Si el mail no llega, reintenta. */
    } finally {
      // Se muestra SIEMPRE, haya existido la cuenta o no: decir "ese mail no existe"
      // es contarle a cualquiera quién tiene cuenta en Atlas.
      setResetEnviado(true);
      setResetLoading(false);
    }
  };

  // ¿Hay un formulario de tarjeta de Rebill montado AHORA? Es la condición que congela
  // el layout: cambiar de árbol lo desmonta, y remontarlo le borra al cliente la
  // tarjeta a medio tipear y abre una segunda sesión de checkout contra el store de
  // módulo del SDK, que es único. Sale de `cobraConRebill` —la misma función con la que
  // el paso de pago decide montarlo— para que montar y congelar no puedan separarse.
  //
  // Se calcula acá arriba, antes de los `return` tempranos, porque de ella depende un
  // hook: `!!planCatalogo` es lo mismo que `!esCustom`, pero está resuelto desde el
  // primer render (`esCustom` se define más abajo, después de los guards).
  const hayTarjetaDeRebillMontada =
    step === 'pago' && cobraConRebill(pais, !!planCatalogo);

  // Qué árbol se dibuja. Antes los dos vivían en el DOM y los tapaba el CSS
  // (`md:hidden` / `hidden md:flex`), así que en el paso de pago se montaban DOS
  // `<rebill-checkout>`: dos inicializaciones del SDK (~3 requests cada una) peleando
  // por un store de módulo que es uno solo. Ahora elige JS y se dibuja uno.
  // Ver ADR-2026-09-22-un-solo-arbol-en-el-alta-por-link.
  const debajoDeMd = useDebajoDeMd(hayTarjetaDeRebillMontada);

  // Lo que pasa adentro del recuadro de Rebill, al embudo. listo/lento/roto se cuentan
  // una vez por plan y carga, igual que checkout_view; envíos y rechazos, cada vez
  // (son intentos). Va antes de los `return` tempranos: es un hook.
  // Ver ADR-2026-10-16-pago-directo-espera-y-medicion.
  const alEstadoDeRebill = useCallback(
    (estado: EstadoDelCheckoutRebill, detalle?: string) => {
      if (!planSlug) return;
      if (estado === 'listo') {
        // En el celular el form arranca debajo del header y del resumen del plan: se
        // baja hasta él una vez que se puede tipear. En desktop el panel ya lo muestra.
        if (debajoDeMd) pasoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (!esPrimeraVezEnEstaCarga(`checkout_listo:${planSlug}`)) return;
        void trackEvent('checkout_listo', {
          ms_desde_registro: msDesde('registro_enviado'),
          ms_desde_pago_visible: msDesde('pago_visible'),
        });
        marcarMomento('checkout_listo');
        return;
      }
      if (estado === 'lento' || estado === 'roto') {
        if (!esPrimeraVezEnEstaCarga(`checkout_${estado}:${planSlug}`)) return;
        void trackEvent(estado === 'lento' ? 'checkout_lento' : 'checkout_roto', {
          ms_desde_pago_visible: msDesde('pago_visible'),
        });
        return;
      }
      if (estado === 'tarjeta_enviada') {
        void trackEvent('tarjeta_enviada', { ms_desde_listo: msDesde('checkout_listo') });
        return;
      }
      void trackEvent('pago_rechazado_form', {
        mensaje: (detalle ?? '').slice(0, 200) || null,
      });
    },
    [planSlug, trackEvent, debajoDeMd],
  );

  // ── Resolviendo un slug que no es del catálogo (puede ser un plan a medida) ──
  if (planCustomEstado === 'cargando') {
    return (
      <div className="min-h-screen bg-surface-bg flex items-center justify-center p-6">
        <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
      </div>
    );
  }

  // ── El plan existe pero no se pudo leer su precio (Stripe/server caídos) ──
  // Mensaje distinto al de link inválido a propósito: el link ES válido y reintentar sirve.
  // Decirle "no existe" a un cliente que tiene el link correcto lo manda a molestarnos por
  // algo que se arregla solo en dos minutos.
  if (planCustomEstado === 'error') {
    return (
      <div className="min-h-screen bg-surface-bg flex items-center justify-center p-6 text-center">
        <div className="max-w-md">
          <h1 className="text-[22px] font-semibold text-brand-black/90 mb-2">
            No pudimos cargar tu plan
          </h1>
          <p className="text-[15px] text-neutral-500">
            Fue un problema momentáneo de nuestro lado. Actualizá la página en unos minutos; si
            sigue igual, escribinos.
          </p>
        </div>
      </div>
    );
  }

  // ── Plan inválido (slug malo, y tampoco es un plan a medida) ──
  if (!planCatalogo && !planCustom) {
    return (
      <div className="min-h-screen bg-surface-bg flex items-center justify-center p-6 text-center">
        <div className="max-w-md">
          <h1 className="text-[22px] font-semibold text-brand-black/90 mb-2">
            Link no válido
          </h1>
          <p className="text-[15px] text-neutral-500">
            Este link de alta no corresponde a un plan disponible. Pedile a tu
            contacto en Atlas el link correcto.
          </p>
        </div>
      </div>
    );
  }

  // ── Vista unificada del plan (catálogo público o a medida) ──
  // De acá para abajo el render no distingue: usa `planNombre`, `planFeatures` y `priceLabel`.
  const esCustom = !planCatalogo;
  // Los planes del catálogo se llaman "Ultra Nivel 1" y el copy pone el "Plan" adelante. Un
  // plan a medida puede venir nombrado "Plan de Prueba", que daba "Suscribite al Plan Plan de
  // Prueba" → se le saca el prefijo para los copys que ya dicen "plan".
  const planNombre = planCatalogo
    ? planCatalogo.name
    : nombrePlanSinPrefijo(planCustom!.nombre);
  // Los planes a medida tienen su propia lista (ver featuresPlanCustom): la cuota negociada en
  // créditos primero, porque imágenes y videos salen del mismo pozo.
  const planFeatures = planCatalogo
    ? planCatalogo.features
    : featuresPlanCustom(planCustom!.maxFotosMes);
  // Lo que se le manda a /stripe/checkout: id del catálogo o slug del plan a medida.
  const planParaCheckout = planCatalogo ? planId! : planCustom!.slug;

  // `planCatalogo.price` es el precio en ARS; para el resto de los países se resuelve
  // con la misma tabla que usa el checkout normal (módulo compartido). Los planes a medida
  // NO pasan por ahí: su precio ya viene de Stripe en su moneda final.
  const price = planCatalogo
    ? getPlanPriceForPais(planCatalogo.id, planCatalogo.price, pais)
    : 0;
  // ¿Cobra por el formulario embebido de Rebill? Es el MISMO predicado que decide si se
  // monta `<rebill-checkout>` y si el layout se congela: sale una sola vez, de
  // `cobraConRebill`, y de acá para abajo nadie lo vuelve a derivar a mano. Incluye que
  // los planes a medida siempre cobran por Stripe (Rebill ni tiene el plan cargado).
  const cobraPorRebill = cobraConRebill(pais, !esCustom);
  // Guard `> 0` además de `< 1`: si la DB tuviera `multiplicador = 0` (dato malo),
  // sin el guard `Math.floor(price*0)` mostraría "$0"; con el guard cae a precio
  // full. `Math.round` en vez de `floor` para redondear parejo.
  // ¿Este alta admite el cupón "Hot Sale"? La elegibilidad por plan e historial la
  // decide `cambioDePlanAdmiteDescuento`, la MISMA función que usan el catálogo y
  // `/checkout`, y que el servidor de pagos ahora recalcula por su cuenta al cobrar
  // (ADR-2026-08-19-alta-por-link-respeta-la-regla-del-cupon).
  //
  // Acá el plan de origen es SIEMPRE `gratis`: quien ya tiene plan pago no llega a
  // esta pantalla (`resolveStepForUser` lo manda a `redirect` para no re-cobrarlo).
  // Lo que sí llega es el EX cliente —se dio de baja, quedó en `gratis`— y por eso
  // hace falta `yaFueClientePago`: sin él veía el 30% que el server ya no aplica.
  //
  // Antes esto era una lista negra (`PLANES_SIN_CUPON = ['emprendimiento']`) con el
  // criterio de que un plan nuevo heredara la promo. Se invierte a propósito: ahora
  // la lista blanca vive del lado del server, así que un plan nuevo que acá heredara
  // el descuento se cobraría igual a precio de lista. Que la promo NO se herede sola
  // es justamente lo que mantiene alineado lo que se muestra con lo que se cobra.
  //
  // Además del plan, el cupón exige ARGENTINA: el "Hot Sale" es una promo local y la
  // landing solo muestra precio tachado cuando el país es AR (`originalPrice:
  // countryCode === "AR" ? … : null`). Sin este guard, un chileno vería "el primer
  // mes" con un descuento que su cobro por Stripe no aplica. Ver ADR-592.
  //
  // Los planes a medida tampoco admiten cupón, por una razón propia: su precio YA es
  // el negociado con ese cliente. Encimarle la promo general sería un descuento sobre
  // un descuento que nadie acordó. El payment server lo bloquea también del lado del
  // server, así que no depende de que el front se porte bien.
  // `cobraPorRebill` es exactamente `!esCustom && paisFlags(pais).isArgentina`, que es
  // lo que decía esta condición: se reusa para que el predicado "Argentina con plan del
  // catálogo" tenga UNA sola definición en el archivo. El `&&` sigue cortando antes del
  // `planCatalogo!`, que sólo se evalúa cuando el plan es del catálogo.
  // `exClienteParaElPrecio` y no `yaFueClientePago` a secas: en el paso de crear cuenta
  // todavía no hay cuenta que consultar, y el default seguro del state (`true`) hacía que
  // la pantalla mostrara precio de lista a quien la landing había mandado con el 30%. En
  // el paso que COBRA devuelve el valor de la base tal cual, así que el `couponCode` que
  // viaja a Rebill no cambia.
  const planAdmiteCupon =
    cobraPorRebill &&
    cambioDePlanAdmiteDescuento(
      'gratis',
      planCatalogo!.id,
      exClienteParaElPrecio(step, authMode, yaFueClientePago),
    );

  const mult =
    planAdmiteCupon &&
    coupon &&
    coupon.multiplicador > 0 &&
    coupon.multiplicador < 1
      ? coupon.multiplicador
      : null;
  const hasDiscount = mult !== null;
  const bigPrice = mult !== null ? Math.round(price * mult) : price;
  // Precio a medida: viene de Stripe ya en su moneda, así que NO pasa por la conversión por
  // país (`formatPriceForPais` asume un monto en ARS y lo convierte).
  const priceLabel = esCustom
    ? formatPrecioCustom(planCustom!.precio, planCustom!.moneda)
    : formatPriceForPais(bigPrice, pais); // grande (con descuento si hay)
  const fullPriceLabel = esCustom
    ? priceLabel
    : formatPriceForPais(price, pais); // full ("Después … / mes")

  // ── Pago del exterior: Stripe por redirect ───────────────────────────
  // Argentina cobra con el checkout embebido de Rebill (in-app). El resto del
  // mundo va a Stripe, que es un REDIRECT: se le pide la URL al payment server
  // y se manda al usuario para afuera. Es el mismo endpoint y el mismo shape
  // que usa `Checkout.tsx` para los no-argentinos; acá no se reinventa nada.
  //
  // OJO con la consecuencia de UX: al ser redirect, el usuario SE VA de esta
  // página, así que la pantalla de éxito de este flujo (`step === 'exito'`) no
  // se muestra para el exterior. Vuelve adonde esté configurado el
  // `success_url` de la Checkout Session.
  const irAStripe = async () => {
    if (stripeLoading) return;
    setStripeLoading(true);
    setError(null);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Sesión no encontrada. Volvé a iniciar sesión.');

      const url = await fetchStripeCheckoutUrl({
        user_id: user.id,
        // Id del catálogo ('enterprise2') o slug del plan a medida ('zara'): el payment
        // server resuelve los dos y saca el price que corresponda.
        plan: planParaCheckout,
        // SIN cupón, por dos razones. Primero: el Hot Sale es una promo
        // argentina y este camino es justamente el del exterior, así que
        // no corresponde. Segundo, aunque correspondiera, no se podría
        // mandar bien: el RPC `get_alta_empresa_coupon` devuelve solo
        // `(codigo, multiplicador)` y el endpoint espera `cupon_id`.
        // Mandar `con_descuento: true` sin el id sería un payload a medio
        // armar contra un endpoint que COBRA. Antes de eso, precio full.
      });
      // Se creó la Checkout Session de Stripe → estamos por redirigir. Es el último
      // evento que el app puede emitir para el exterior (después el usuario se va a
      // Stripe): separa "vio el botón" (checkout_view) de "llegó a crear el pago".
      // Lo ESPERAMOS antes de redirigir: el redirect cross-origin cancela el
      // insert en vuelo, así que fire-and-forget lo perdería casi siempre. Con
      // tope de tiempo para no bloquear el pago si la DB está lenta/caída — el
      // pago manda, el tracking es best-effort.
      // Meta: pasó a cargar la tarjeta (la página de Stripe es de ellos; esto es lo último
      // que la app ve del exterior). `fbq` encola el envío; no se espera.
      trackMeta('AddPaymentInfo', parametrosMetaDelPlan());
      await Promise.race([
        trackEvent('checkout_created'),
        new Promise((resolve) => setTimeout(resolve, 1500)),
      ]);
      window.location.href = url;
    } catch (err: any) {
      setError(err?.message || 'No se pudo iniciar el pago.');
      setStripeLoading(false);
    }
  };

  // ── Resumen del plan (columna izquierda / cabecera mobile) ──
  // OJO: son funciones que se LLAMAN ({renderX()}), no componentes (<X />).
  // Definirlos como componentes acá adentro haría que React los re-monte en
  // cada render (nueva identidad de función) → el input pierde el foco al tipear.
  const renderPlanSummary = () => (
    <>
      <div className="max-w-[440px]">
        <p className="text-[15px] text-neutral-500 mb-1">
          Suscribite al Plan {planNombre}
        </p>
        <div className="flex items-baseline gap-2.5">
          <span className="text-[46px] leading-none font-bold text-brand-black/90 tracking-tight">
            {priceLabel}
          </span>
          <span className="text-[14px] text-neutral-500">
            {hasDiscount ? 'el primer mes' : 'por mes'}
          </span>
        </div>
        {hasDiscount && (
          <p className="text-[13px] text-neutral-600 mt-2">
            Después {fullPriceLabel} / mes
          </p>
        )}
        <ul className="mt-10 space-y-3">
          {planFeatures.map((feature, i) => (
            <li
              key={i}
              className="flex items-center text-[14px] text-neutral-600"
            >
              <CheckCircle2 className="w-4 h-4 text-brand-primary mr-2.5 flex-shrink-0" />
              {feature}
            </li>
          ))}
        </ul>
      </div>
      <p className="mt-auto pt-10 text-[12px] text-neutral-400">
        Se renueva automáticamente cada mes. Cancelás cuando quieras.
      </p>
    </>
  );

  // ── Contenido del paso actual (columna derecha) ──
  const renderStepContent = () => {
    if (step === 'loading') {
      return (
        <div className="flex items-center justify-center gap-2 py-10 text-[15px] text-neutral-400">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
        </div>
      );
    }

    // No se pudo leer el estado de la cuenta → no se cobra. Mismo criterio (y
    // casi el mismo texto) que la puerta de `Checkout.tsx`: sin saber si ya hay
    // una suscripción activa, seguir sería arriesgarse a duplicarla.
    if (step === 'error-estado') {
      return (
        <div className="py-10 text-center">
          <p className="text-[15px] text-brand-black/90">
            {/* Dos cosas terminan acá y NO dicen lo mismo: no poder leer el estado de
                la cuenta (la puerta que no cobra) y no poder crear la empresa. El texto
                por defecto es el de la puerta; el otro trae su propio motivo, que
                `aplicarEstadoDeCuenta` se encarga de limpiar al entrar por su lado. */}
            {error ??
              'No pudimos verificar el estado de tu cuenta, así que no seguimos con el cobro para no duplicar una suscripción.'}
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-4 inline-flex h-10 items-center justify-center rounded-md bg-brand-primary px-4 text-[15px] font-medium text-white shadow-sm transition-colors hover:bg-brand-primary-hover"
          >
            Reintentar
          </button>
        </div>
      );
    }

    if (step === 'exito') {
      return (
        <div>
          <h2 className="text-[24px] font-bold text-brand-black/90 tracking-tight mb-2">
            ¡Listo! Tu plan está activo
          </h2>
          <p className="text-[15px] text-neutral-500 mb-8">
            Ya podés empezar a usar Atlas con todo tu plan {planNombre}.
          </p>
          <button
            type="button"
            // El destino sale de `destinoPostPago`, la MISMA función que usa
            // `/checkout`. Acá había una copia que se había quedado atrás en dos cosas:
            // no ofrecía nunca la facturación AFIP, y al mandar a la capacitación Ultra
            // no pasaba `conFacturacion`, que es el flag con el que ESA pantalla
            // encadena la oferta después. Resultado: el que pagaba por este link no veía
            // el ofrecimiento nunca, con AFIP ya en producción.
            // Ver ADR-2026-09-22-el-destino-post-pago-es-uno-solo.
            //
            // `home` es el CUESTIONARIO: la empresa se creó sola para poder cobrar en
            // un paso y todavía no se le preguntó nada.
            // Ver ADR-2026-09-22-el-alta-por-link-cobra-primero-y-pregunta-despues.
            //
            // `homeEsFijo: true` es lo que hace que ese destino SOBREVIVA las pantallas
            // intermedias. Sin eso era código muerto para un argentino: con AFIP en
            // producción siempre sale por `/facturacion-oferta` (o antes por
            // `/capacitacion-ultra`), y esas pantallas resolvían el estudio por su
            // cuenta — o sea que el que pagaba por el link entraba al estudio sin
            // cuestionario y recién lo devolvía ahí el rebote de la app en la visita
            // siguiente. Sólo lo manda ESTA pantalla: el `home` de `/checkout` depende
            // del grupo del usuario y no se propaga a propósito
            // (ver `resolverHomePostPago`).
            // Ver ADR-2026-09-22-el-destino-post-pago-sobrevive-la-cadena.
            //
            // Sólo se llega a este paso con un cobro de Rebill, que exige plan del
            // catálogo, así que `esCustom` no puede ser true acá.
            onClick={() => {
              const destino = destinoPostPago({
                planId,
                isArgentina: cobraPorRebill,
                devTester: userGroupDevTester,
                home: POST_PAY_DEST,
                homeEsFijo: true,
              });
              navigate(destino.ruta, {
                replace: true,
                ...(destino.state ? { state: destino.state } : {}),
              });
            }}
            className="inline-flex items-center justify-center py-3 px-6 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
          >
            Empezar a usar Atlas
          </button>
        </div>
      );
    }

    if (step === 'pago') {
      // RED DE SEGURIDAD DEL PAÍS. Al sacar el paso de datos se fue el `CountrySelect`,
      // que era donde el cliente elegía el país — y el país es lo que decide si se cobra
      // en ARS por Rebill o en USD por Stripe. En la práctica casi siempre está resuelto
      // (229 de 230 llegadas vienen de la landing, que manda `?pais=`), y si no, queda
      // el que el backend dedujo de la IP al crear la empresa. Pero cuando NINGUNO de
      // los dos pudo decir nada, preguntar es mejor que cobrar en la moneda equivocada:
      // el selector aparece sólo en ese caso, y sólo hasta que elija.
      // Ver ADR-2026-09-22-el-alta-por-link-cobra-primero-y-pregunta-despues.
      if (!pais) {
        return (
          <div className="space-y-4">
            <div>
              <h2 className="text-[22px] font-semibold text-brand-black/90 mb-1">
                ¿Desde qué país nos escribís?
              </h2>
              <p className="text-[14px] text-neutral-500">
                Lo necesitamos para cobrarte en tu moneda.
              </p>
            </div>
            <CountrySelect
              value={pais}
              onChange={(elegido) => {
                setPais(elegido);
                // La empresa ya se creó (con 'Otro', que es lo que había): si el cliente
                // nos dice su país acá, hay que GUARDARLO. Si no, la moneda del cobro y
                // el país de la cuenta quedan diciendo cosas distintas — y de
                // `company_profiles.pais` dependen después la facturación AFIP y los
                // precios del Paywall. Best-effort: lo que manda para cobrar es el
                // estado de la pantalla, que ya quedó bien.
                void guardarPaisElegido(elegido);
              }}
              required
            />
          </div>
        );
      }
      // Argentina → Rebill embebido (in-app). Resto → Stripe (redirect).
      // Misma regla que `Checkout.tsx`; antes esta pantalla montaba Rebill para
      // todo el mundo y le mostraba precios en ARS a un chileno. Ver ADR-592.
      //
      // Se pregunta por `hayTarjetaDeRebillMontada` y no por `cobraPorRebill` —adentro de
      // este `if` son lo mismo— para que montar el formulario y congelar el layout
      // salgan de la MISMA expresión y no puedan separarse nunca.
      if (!hayTarjetaDeRebillMontada) {
        return (
          <div className="space-y-4">
            <p className="text-[14.5px] text-[#3C3C43]/70 leading-snug">
              Te vamos a llevar a Stripe para completar tu suscripción de forma
              segura. Volvés a Atlas apenas termines.
            </p>
            <button
              type="button"
              onClick={irAStripe}
              disabled={stripeLoading}
              className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 text-white text-[15px] font-semibold shadow-sm transition-colors"
            >
              {stripeLoading ? 'Redirigiendo…' : 'Continuar al pago'}
            </button>
          </div>
        );
      }
      // Mismo loader que `Checkout.tsx`: hasta no saber si hay cupón NO se monta el
      // checkout — un `couponCode` que después cambia lo destruye y lo recrea, con la
      // tarjeta a medio tipear adentro. En la práctica casi nunca se ve (el RPC del
      // cupón es 1 request y la puerta de la cuenta son 3 en serie), pero el que lo
      // sufre es alguien que está pagando. El gate siempre abre: ver `couponResolved`.
      if (!couponResolved) {
        return (
          <div className="flex items-center justify-center gap-2 py-10 text-[15px] text-neutral-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
          </div>
        );
      }
      // Sin título extra: va directo al form de Rebill, igual que el checkout.
      return (
        <RebillCardPayment
          // Solo se llega acá con `cobraPorRebill`, que exige !esCustom → planCatalogo existe.
          // Rebill no conoce los planes a medida (son exterior/Stripe por definición).
          planId={planCatalogo!.id}
          // Emprendimiento (`/alta-empresa/basico`) está en PLANES_SIN_CUPON →
          // `hasDiscount` queda false y acá viaja `couponCode: null`, con lo que
          // el componente exige precio de lista y limpia cualquier descuento
          // arrastrado de otro plan en la misma página
          // (ADR-2026-08-24-limpiar-descuento-rebill-sin-recargar).
          couponCode={hasDiscount ? (coupon?.codigo ?? null) : null}
          onSuccess={(_r: RebillCheckoutResult) => {
            // `pago_ok` = pagó DE VERDAD, a diferencia de `conversion`, que
            // solo dice que llegó al formulario. Ver ADR-593.
            //
            // Solo cubre Argentina (Rebill es in-app, así que el éxito ocurre
            // acá). El camino Stripe es un REDIRECT: el usuario se va de la
            // página y este código nunca corre. Para cubrirlo hay que emitir el
            // evento desde el workflow `stripe webhook` de n8n, en
            // `invoice.payment_succeeded`. Queda pendiente y anotado.
            void trackEvent('pago_ok');
            setStep('exito');
          }}
          onError={(msg) => setError(msg)}
          onEstado={alEstadoDeRebill}
          // Solo en el alta nueva; quien vuelve con la empresa creada deja que el
          // componente lo consulte (undefined).
          prefetchedCtx={ctxDelCobro}
        />
      );
    }

    // Creando la empresa: el reemplazo del formulario de datos. Dura lo que tarda el
    // backend (tope de 30s en `createCompany`), y el cliente ve que su cuenta se está
    // armando en vez de tener que llenar cuatro campos más antes de poder pagar.
    if (step === 'creando') {
      return (
        <div className="flex items-center justify-center gap-2 py-10 text-[15px] text-neutral-400">
          <Loader2 className="w-4 h-4 animate-spin" /> Preparando tu cuenta…
        </div>
      );
    }

    // step === 'auth'
    return (
      <form onSubmit={handleAuth} noValidate className="space-y-5">
        <div>
          <h2 className="text-[22px] font-semibold text-brand-black/90 mb-1">
            {authMode === 'signup' ? 'Creá tu cuenta' : 'Iniciá sesión'}
          </h2>
          <p className="text-[14px] text-neutral-500">
            {authMode === 'signup'
              ? 'Empezá tu suscripción al plan ' + planNombre + '.'
              : 'Entrá con tu cuenta para continuar.'}
          </p>
        </div>
        {/* Google primero: es el 38% de las altas de la app, y quien ya tiene cuenta de
            Google no puede entrar por el formulario de abajo. El botón usa el lenguaje
            visual de ESTA pantalla (alto y radio del submit de acá), no el del
            onboarding: conviven en el mismo formulario.
            Ver ADR-2026-09-22-google-en-el-alta-por-link. */}
        <button
          type="button"
          onClick={handleGoogle}
          disabled={googleLoading || authLoading}
          className="w-full h-[var(--btn-h)] px-8 inline-flex items-center justify-center gap-2.5 border border-neutral-200 bg-surface-card hover:bg-neutral-50 text-brand-black/90 rounded-md font-medium text-[15px] shadow-sm transition-colors disabled:opacity-50"
        >
          {googleLoading ? (
            <Loader2 className="w-4 h-4 animate-spin text-neutral-400" />
          ) : (
            <Icon name="color/google" size={18} />
          )}
          {authMode === 'signup' ? 'Registrarme con Google' : 'Entrar con Google'}
        </button>
        {embebido && <AvisoGoogleEnApp embebido={embebido} pantalla="alta_empresa" />}
        {/* Sólo cuando el aviso es accionable: en este dispositivo se entró con Google y
            la persona está por tipear una contraseña que puede no existir. Es el
            callejón sin salida que se abre al ofrecer los dos caminos. */}
        {ultimoLogin === 'google' && (
          <p className="text-[13px] text-neutral-500 text-center">
            La última vez entraste con Google.
          </p>
        )}
        <div className="flex w-full items-center gap-3">
          <span className="h-px flex-1 bg-neutral-200" />
          <span className="text-[12px] text-neutral-400">o</span>
          <span className="h-px flex-1 bg-neutral-200" />
        </div>
        {/* Sólo al registrarse: es el único dato del viejo paso de datos que no se
            puede deducir, y el cuestionario post-pago tampoco lo pregunta (lo toma de
            la metadata del signUp). Con Google viene del perfil, así que no se pide. */}
        {authMode === 'signup' && (
          <div>
            <label className="text-[13px] font-medium text-neutral-600 mb-1 block">
              Nombre y apellido
            </label>
            <input
              type="text"
              value={name}
              ref={(el) => {
                camposRef.current.name = el;
              }}
              onChange={(e) => {
                setName(e.target.value);
                if (avisoDeCampo?.campo === 'name') setAvisoDeCampo(null);
              }}
              aria-invalid={avisoDeCampo?.campo === 'name' || undefined}
              className="w-full px-3 py-2.5 border border-neutral-200 rounded-md text-[15px] text-brand-black/90 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors bg-surface-card"
              placeholder="Tu nombre"
              autoComplete="name"
              required
            />
            {avisoDeCampo?.campo === 'name' && (
              <p role="alert" className="mt-1.5 text-[13px] text-red-700">
                {avisoDeCampo.mensaje}
              </p>
            )}
          </div>
        )}
        <div>
          <label className="text-[13px] font-medium text-neutral-600 mb-1 block">
            Email
          </label>
          <div className="relative">
            <Mail className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              ref={(el) => {
                camposRef.current.email = el;
              }}
              type="email"
              value={email}
              aria-invalid={avisoDeCampo?.campo === 'email' || undefined}
              onChange={(e) => {
                setEmail(e.target.value);
                if (avisoDeCampo?.campo === 'email') setAvisoDeCampo(null);
                // Corregir el mail vuelve a habilitar "¿Olvidaste tu contraseña?": sin
                // esto, quien lo escribía mal veía el aviso de "te mandamos las
                // instrucciones" y ya no podía volver a pedirlo en toda la carga.
                setResetEnviado(false);
              }}
              className="w-full pl-9 pr-3 py-2.5 border border-neutral-200 rounded-md text-[15px] text-brand-black/90 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors bg-surface-card"
              placeholder="tu@email.com"
              required
            />
          </div>
          {avisoDeCampo?.campo === 'email' && (
            <p role="alert" className="mt-1.5 text-[13px] text-red-700">
              {avisoDeCampo.mensaje}
            </p>
          )}
        </div>
        <div>
          <label className="text-[13px] font-medium text-neutral-600 mb-1 block">
            Contraseña
          </label>
          <div className="relative">
            <Lock className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              ref={(el) => {
                camposRef.current.password = el;
              }}
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (avisoDeCampo?.campo === 'password') setAvisoDeCampo(null);
              }}
              aria-invalid={avisoDeCampo?.campo === 'password' || undefined}
              className="w-full pl-9 pr-3 py-2.5 border border-neutral-200 rounded-md text-[15px] text-brand-black/90 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors bg-surface-card"
              placeholder="••••••••"
              minLength={6}
              required
            />
          </div>
          {avisoDeCampo?.campo === 'password' && (
            <p role="alert" className="mt-1.5 text-[13px] text-red-700">
              {avisoDeCampo.mensaje}
            </p>
          )}
        </div>
        <button
          type="submit"
          disabled={authLoading}
          className="w-full h-[var(--btn-h)] px-8 inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md font-medium text-[15px] shadow-sm transition-colors disabled:opacity-50"
        >
          {authLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Un momento…
            </>
          ) : authMode === 'signup' ? (
            'Crear cuenta'
          ) : (
            'Iniciar sesión'
          )}
        </button>
        {/* Recuperar la clave SIN salir de la pantalla: irse a /auth perdía el `?pais=`
            (la moneda del precio) y el `?sid=` (el linkeo del embudo). Sólo en la
            solapa de login: en la de registro no tiene sentido. */}
        {authMode === 'signin' &&
          (resetEnviado ? (
            <p className="text-[13px] text-neutral-500 text-center">
              Si existe una cuenta con ese correo, te mandamos las instrucciones para
              restablecer tu contraseña.
            </p>
          ) : (
            <p className="text-[13px] text-neutral-500 text-center">
              <button
                type="button"
                onClick={pedirResetDeClave}
                disabled={resetLoading}
                className="text-brand-primary font-medium hover:underline disabled:opacity-50"
              >
                {resetLoading ? 'Enviando…' : '¿Olvidaste tu contraseña?'}
              </button>
            </p>
          ))}
        <p className="text-[13px] text-neutral-500 text-center">
          {authMode === 'signup' ? '¿Ya tenés cuenta? ' : '¿No tenés cuenta? '}
          <button
            type="button"
            onClick={() => {
              setAuthMode(authMode === 'signup' ? 'signin' : 'signup');
              setAvisoDeCampo(null);
              setError(null);
              setResetEnviado(false);
            }}
            className="text-brand-primary font-medium hover:underline"
          >
            {authMode === 'signup' ? 'Iniciá sesión' : 'Creá una'}
          </button>
        </p>
      </form>
    );
  };

  const renderLogo = (size: 'sm' | 'md' = 'md') => (
    <div className="flex items-center gap-2.5">
      <div
        className={`${size === 'sm' ? 'w-7 h-7' : 'w-9 h-9'} rounded-full bg-brand-black flex items-center justify-center shrink-0`}
      >
        <img
          src="/images/estudio-atlas-logo-solo-blanco.svg"
          alt=""
          className={size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'}
        />
      </div>
      <span
        className={`${size === 'sm' ? 'text-[14px]' : 'text-[16px]'} font-semibold text-brand-black/90`}
      >
        Estudio Atlas
      </span>
    </div>
  );

  const renderErrorBanner = () =>
    error ? (
      <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2.5 text-[15px] text-red-700">
        <AlertCircle className="w-4 h-4 flex-shrink-0" />
        <span>{error}</span>
      </div>
    ) : null;

  return (
    <div
      // En desktop la página es su propio contenedor de scroll con el gutter de
      // la scrollbar RESERVADO (scrollbar-gutter: stable): así, abrir el dropdown
      // de rubro (que se pasa del viewport) NO hace aparecer/desaparecer la barra
      // del body ni corre el layout a la izquierda. Fondo blanco en desktop → el
      // gutter reservado (extremo derecho) queda invisible.
      className="min-h-screen md:h-screen md:overflow-y-auto bg-surface-card"
      style={{ scrollbarGutter: 'stable' }}
    >
      {/* ═══════════ MOBILE (homogéneo con /checkout) ═══════════
          Los dos árboles son EXCLUYENTES y los elige JS, no el CSS: antes convivían en
          el DOM con `md:hidden` / `hidden md:flex` y en el paso de pago se montaban dos
          `<rebill-checkout>`. `key` explícita para que cruzar el breakpoint sea un
          desmontaje y un montaje limpios, y no un div reciclado con hijos ajenos.
          Ver ADR-2026-09-22-un-solo-arbol-en-el-alta-por-link. */}
      {debajoDeMd ? (
      <div key="mobile" className="flex flex-col min-h-screen pb-10">
        {/* Header: logo + "Pago seguro" (igual que el checkout). */}
        <div
          className="px-5 pb-2 flex items-center justify-between"
          style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1.25rem)' }}
        >
          {renderLogo('sm')}
          <div className="flex items-center gap-1 text-[11px] text-neutral-400">
            <Lock className="w-3 h-3" />
            <span>Pago seguro</span>
          </div>
        </div>

        {/* Resumen del plan compacto y centrado + "Ver qué incluye" colapsable
            (features ocultas por defecto), igual que el checkout. */}
        <div className="px-5 pt-7 pb-5 border-b border-neutral-200 text-center">
          <p className="text-[13px] text-neutral-500">Plan {planNombre}</p>
          <p className="text-[28px] font-bold text-brand-black/90 tracking-tight leading-tight mt-1">
            {priceLabel}
            <span className="text-[13px] font-normal text-neutral-400 ml-1">
              {hasDiscount ? 'el primer mes' : '/ mes'}
            </span>
          </p>
          {hasDiscount && (
            <p className="text-[13px] text-neutral-600 mt-0.5">
              Después {fullPriceLabel} por mes
            </p>
          )}
          <button
            type="button"
            onClick={() => setShowFeatures(!showFeatures)}
            className="mt-3 inline-flex items-center gap-1 text-[13px] text-brand-primary hover:text-brand-primary-hover font-medium transition-colors"
          >
            Ver qué incluye
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform ${showFeatures ? 'rotate-180' : ''}`}
            />
          </button>
          {showFeatures && (
            <ul className="mt-3 space-y-2 text-left">
              {planFeatures.map((feature, i) => (
                <li
                  key={i}
                  className="flex items-center text-[13px] text-neutral-600"
                >
                  <CheckCircle2 className="w-3.5 h-3.5 text-brand-primary mr-2 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Contenido del paso (auth / datos / pago). El tope de ancho sólo actúa de
            `md` para arriba: es para cuando el árbol quedó CONGELADO en mobile y la
            ventana es ancha (si no, el form de tarjeta se estira a todo el monitor). */}
        <div ref={pasoRef} className="px-5 pt-5 md:max-w-[520px] md:mx-auto">
          {renderErrorBanner()}
          {renderStepContent()}
        </div>
      </div>
      ) : (
      /* ═══════════ DESKTOP ═══════════
         Las clases `md:` de las dos columnas son la red del congelamiento al revés:
         si el árbol quedó clavado en desktop y el viewport es angosto (entrar al pago
         con el teléfono en horizontal y rotarlo), sin ellas la columna del paso queda
         en 115 px y la tarjeta no se puede tipear. De `md` para arriba el render es
         idéntico al de siempre. */
      <div key="desktop" className="flex min-h-screen">
        {/* Izquierda — resumen del plan */}
        <div className="w-1/2 bg-[#DDDFE6] hidden md:flex flex-col px-10 lg:px-20 py-12">
          <div className="mb-16">
            {renderLogo()}
          </div>
          {renderPlanSummary()}
        </div>
        {/* Derecha — el paso actual */}
        <div className="w-full md:w-1/2 bg-surface-card flex flex-col justify-center px-10 lg:px-20 py-12">
          <div className="w-full mx-auto max-w-[480px]">
            {renderErrorBanner()}
            {renderStepContent()}
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default EnterpriseCheckout;
